import axiosInstance from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { ROLE_CACHE_GC_MS, ROLE_CACHE_STALE_MS } from "@/lib/queryClient";
import { roleCatalog } from "@/config/endpoints";
import type { RoleCatalogEntry } from "@/features/leads/utils/roleColumns";

export type RoleCatalogRole = RoleCatalogEntry & {
  id: number;
  name: string;
  hierarchy: number;
  slug?: string | null;
  acceptsLead: boolean;
  acceptsChild: boolean;
  personalStageCount: number;
  hasElevatedStages: boolean;
  firstStageOrder: number | null;
};

/**
 * EVERY role of the tenant (no users) with the work-pattern metadata the List
 * view lays its role columns out from — see `roleColumns.ts`.
 *
 * Not `useRolesPipelineLite`: that one is hierarchy-scoped (roles BELOW the
 * viewer only), so a counsellor would get no Counsellor column and a visa-team
 * member no Counsellor either. The catalog is unscoped except for the
 * viewer's explicit role exclusions, which the server applies.
 *
 * Returned as `rolesPipeline` too, so the card/table call sites that read
 * `{ rolesPipeline }` for role LABELS swap over with a one-line change.
 */
export default function useRoleCatalog() {
  const query = useQuery({
    queryKey: ["roles-catalog"],
    queryFn: async () => {
      const res = await axiosInstance.get(roleCatalog);
      return (res.data?.roles ?? []) as RoleCatalogRole[];
    },
    // Role shape changes rarely and every writer that changes it already
    // busts this key (role save/delete in FormRoleContext, invalidateUserCaches,
    // invalidateBranchCaches, invalidateLeadAssignment,
    // invalidatePipelineDependentCaches, USER_ACCESS_REFRESH). Long stale /
    // gc windows so remounts across pages don't refetch it — it was 175k
    // GETs a day under the 3-min default plus the socket bundle's prefix bust.
    staleTime: ROLE_CACHE_STALE_MS,
    gcTime: ROLE_CACHE_GC_MS,
  });
  return {
    roles: query.data,
    rolesPipeline: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
